import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { UserProfile } from './users';
import { getUserById, useUserUpdates, loadProfiles } from './users';
import { hydrateProfileFromAuth } from '@/lib/auth';
import { setMonitoringUser } from '@/lib/monitoring';
import { setAnalyticsUser, track, AnalyticsEvent } from '@/lib/analytics';
import { registerPush, unregisterPush } from '@/lib/push';


/** Load the signed-in profile, then every public profile used across the app. */
async function bootstrapProfiles(user: Parameters<typeof hydrateProfileFromAuth>[0]) {
  await loadProfiles(true);
  await hydrateProfileFromAuth(user);
}

interface SessionState {
  currentUserId: string | null;
  currentUser: UserProfile | undefined;
  /** Legacy no-op kept for backwards compatibility with older callers. */
  login: (userId: string) => void;
  logout: () => void;
}

// Keep context stable across HMR by attaching to globalThis
const SESSION_CTX_KEY = '__nextchamp_session_ctx__';
const SessionContext: React.Context<SessionState | null> =
  (globalThis as any)[SESSION_CTX_KEY] ??= createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [booted, setBooted] = useState(false);
  useUserUpdates();

  useEffect(() => {
    // 1) Subscribe to auth changes FIRST to avoid missing an event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        // Defer to avoid Supabase deadlock inside the auth callback
        setTimeout(() => {
          void bootstrapProfiles(session.user).finally(() => setCurrentUserId(session.user.id));
        }, 0);
      } else {
        setCurrentUserId(null);
      }
    });

    // 2) THEN validate the persisted auth token with the backend.
    supabase.auth.getUser().then(({ data: { user }, error }) => {
      if (error) {
        setCurrentUserId(null);
        setBooted(true);
        return;
      }
      if (user) {
        void bootstrapProfiles(user).finally(() => {
          setCurrentUserId(user.id);
          setBooted(true);
        });
      } else {
        setCurrentUserId(null);
        setBooted(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Legacy shim — real login happens through supabase.auth.signInWithPassword
  const login = useCallback((_userId: string) => { /* no-op */ }, []);

  const logout = useCallback(() => {
    void track(AnalyticsEvent.LOGOUT);
    void unregisterPush();
    setMonitoringUser(null);
    void setAnalyticsUser(null);
    setCurrentUserId(null);
    void supabase.auth.signOut();
  }, []);

  const currentUser = currentUserId ? getUserById(currentUserId) : undefined;

  // Identify the user for crash reporting + analytics, and register the device
  // for push notifications once a session is available.
  useEffect(() => {
    if (!currentUser) return;
    setMonitoringUser({ id: currentUser.id, role: currentUser.role, plan: currentUser.plan });
    void setAnalyticsUser({ id: currentUser.id, role: currentUser.role, plan: currentUser.plan });
    void registerPush();
  }, [currentUser?.id, currentUser?.role, currentUser?.plan]);


  if (!booted) {
    // Prevent flashing the Welcome screen while restoring persisted session
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <SessionContext.Provider value={{ currentUserId, currentUser, login, logout }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
